/**
 * TipTapEditor Component
 * Rich text editor using TipTap with the SimpleEditor template toolbar
 */
"use client";

import { useEffect } from "react";
import { EditorContent, EditorContext, useEditor } from "@tiptap/react";

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Placeholder } from "@tiptap/extension-placeholder";

// --- UI Primitives ---
import { Spacer } from "@/components/tiptap-ui-primitive/spacer";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "@/components/tiptap-ui-primitive/toolbar";

// --- Tiptap Node Styles ---
import "@/components/tiptap-node/blockquote-node/blockquote-node.scss";
import "@/components/tiptap-node/list-node/list-node.scss";
import "@/components/tiptap-node/heading-node/heading-node.scss";
import "@/components/tiptap-node/paragraph-node/paragraph-node.scss";

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "@/components/tiptap-ui/heading-dropdown-menu";
import { ListDropdownMenu } from "@/components/tiptap-ui/list-dropdown-menu";
import { BlockquoteButton } from "@/components/tiptap-ui/blockquote-button";
import { MarkButton } from "@/components/tiptap-ui/mark-button";
import { UndoRedoButton } from "@/components/tiptap-ui/undo-redo-button";

// --- Styles ---
import "@/components/tiptap-templates/simple/simple-editor.scss";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  className?: string;
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Word Count Utility
// ---------------------------------------------------------------------------

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

// ---------------------------------------------------------------------------
// Toolbar Component
// ---------------------------------------------------------------------------

function EditorToolbar() {
  return (
    <Toolbar>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3]} />
        <ListDropdownMenu types={["bulletList", "orderedList"]} />
        <BlockquoteButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
      </ToolbarGroup>

      <Spacer />
    </Toolbar>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function TipTapEditor({
  content,
  onChange,
  className,
  placeholder = "Start writing...",
}: TipTapEditorProps) {
  const editor = useEditor({
    immediatelyRender: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Chapter content editor",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight.configure({ multicolor: true }),
      Typography,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when prop changes externally
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Get word count from editor text
  const wordCount = editor ? countWords(editor.getText()) : 0;
  const wordLabel = wordCount === 1 ? "word" : "words";

  if (!editor) {
    return (
      <div className={className}>
        <div className="simple-editor-wrapper chapter-editor-wrapper">
          <div
            className="simple-editor-content"
            style={{
              minHeight: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Loading editor...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className="simple-editor-wrapper chapter-editor-wrapper"
        data-testid="tiptap-editor"
      >
        <EditorContext.Provider value={{ editor }}>
          <EditorToolbar />
          <EditorContent
            editor={editor}
            role="presentation"
            className="simple-editor-content"
          />
        </EditorContext.Provider>

        {/* Word count footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50">
          <span data-testid="word-count" className="text-sm text-slate-500">
            {wordCount} {wordLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
